import ApiClient from 'ApiClient';
import { useDesignStore } from 'context/DesignContext';
import { useSelectedDesignStore } from 'context/SelectedDesignContext';
import { toast } from 'react-hot-toast';
import { ApplyGcodeSettingFromTemplate, cloneDesignByDesignId, removeAdditions, saveDesign } from 'services/designService';
import { pushDesignToStack, resetStackWithDesign } from "components/Viewer3d/UndoStack";
import { DesignCategory, DesignType, InsoleDesign } from 'models/InsoleDesign';
import { Addition } from "models/Addition";
import { ConditionSide, Insole } from "models/Insole";
import { AutoProperties } from "models/AutoProperties";
import { useViewerStore } from 'context/ViewerContext';
import { User } from 'models/User';
import { LOCAL_STORAGE_KEYS } from './consts';
import { showAlertDialog } from 'components/Dialog/dialog';
import { UpdateUnsavedPrintCounts } from 'services/Printerservice';
import { hasPermission, Permissions } from 'auth/permissions';
import { EXHIBITION_STORAGE_KEY, ExhibitionSessionState } from 'models/ExhibitionSession';

let autosaveTimeout: NodeJS.Timeout | null = null;
const api = new ApiClient();
let isShowingSaveDialog = false


export type SaveSettings = {
  newAddition: boolean,
  updateAddition: boolean,
  removeAddition: boolean,
  updateInsoleMesh: boolean,
  updateDesignInfo: boolean,
  updateDesignCategory: boolean,
  updateAutoProperties: boolean,
  replaceAdditons: boolean,
  isUndo: boolean,
  now: boolean
}

export enum ActionType {
  newAddition,
  removeAddition,
  updateAddiiton,
  updateInsole,
  updateDesignInfo,
  others
}

export function getDefaultSaveSettings(): SaveSettings {
  return {
    newAddition: false,
    updateAddition: false,
    removeAddition: false,
    updateInsoleMesh: false,
    updateDesignInfo: false,
    updateDesignCategory: false,
    updateAutoProperties: false,
    replaceAdditons: false,
    isUndo: false,
    now: false,
  };
}

export function getActionTypeBySaveSettings(settings?: Partial<SaveSettings>): ActionType {
  let actionType = ActionType.others;
  if (settings?.newAddition !== undefined && settings?.newAddition === true) {
    actionType = ActionType.newAddition;
  } else if (settings?.updateAddition !== undefined && settings?.updateAddition === true) {
    actionType = ActionType.updateAddiiton;
  } else if (settings?.removeAddition !== undefined && settings?.removeAddition === true) {
    actionType = ActionType.removeAddition;
  } else if (settings?.updateInsoleMesh !== undefined && settings?.updateInsoleMesh === true) {
    actionType = ActionType.updateInsole;
  }

  if (settings?.updateDesignCategory !== undefined && settings?.updateDesignCategory === true) {
    actionType = ActionType.updateDesignInfo;
  }
  return actionType;
}

function hasMeaningfulChange(settings?: Partial<SaveSettings>): boolean {
  if (!settings) return false;
  return (
    settings.newAddition ||
    settings.updateAddition ||
    settings.removeAddition ||
    settings.updateInsoleMesh ||
    settings.updateDesignInfo ||
    settings.updateAutoProperties ||
    settings.replaceAdditons ||
    settings.updateDesignCategory
  ) ?? false;
}

export let saveSettings = getDefaultSaveSettings();

export const scheduleAutosave = async (content: string, settings?: Partial<SaveSettings>) => {
  var designId = useSelectedDesignStore.getState().designId;
  const exhibitionRaw = localStorage.getItem(EXHIBITION_STORAGE_KEY);
  if (exhibitionRaw) {
    try {
      const exhibition = JSON.parse(exhibitionRaw) as ExhibitionSessionState;
      if (designId === exhibition.showcaseDesignId) return;
    } catch {
      localStorage.removeItem(EXHIBITION_STORAGE_KEY);
    }
  }
  await UpdateUnsavedPrintCounts(api);
  if (designId < 1) return;
  const currentDesign = useDesignStore.getState().insoleDesign;
  if (!currentDesign) return;
  const actionType = getActionTypeBySaveSettings(settings);
  const isMeaningful = hasMeaningfulChange(settings);

  if (autosaveTimeout) clearTimeout(autosaveTimeout);
  if (settings?.isUndo) saveSettings = getDefaultSaveSettings();
  Object.assign(saveSettings, settings);

  if (isMeaningful && currentDesign.designType === 'Template') {
    const previousDesign = useDesignStore.getState().previousDesign;
    const previousDesignId = useSelectedDesignStore.getState().previousDesignId;
    if (!previousDesign || previousDesignId == -1) return;
    await applyTemplate(currentDesign, previousDesign, actionType);
    return;
  };
  if (isShowingSaveDialog) return;


  const currentUser = new User(JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.USER) || '{}'));
  const editingForeignDesign = currentUser.isAdmin && currentDesign.createdBy?.id !== currentUser.id;
  const mustClone = editingForeignDesign && !hasPermission(currentUser, Permissions.DesignEditAll);
  if (mustClone && !isMeaningful) {
    if (saveSettings?.now) saveSettings = getDefaultSaveSettings();
    return;
  }
  if (saveSettings?.now) {
    if (!settings?.isUndo && isMeaningful) pushDesignToStack(currentDesign, saveSettings);
    if (editingForeignDesign) {
      var saveAsNewForAdmin = mustClone ? true : restoreSaveAsNewForAdmin();
      if (saveAsNewForAdmin === null && isMeaningful) {
        saveAsNewForAdmin = await showSaveAlertDialog();
        storeSaveAsNewForAdmin(saveAsNewForAdmin);
        isShowingSaveDialog = false;
      }
      if (saveAsNewForAdmin && !settings?.isUndo) {
        const clonedDesignId = await cloneDesignByDesignId(api, designId);
        if (clonedDesignId) {
          designId = clonedDesignId;
          await saveCurrentDesign(currentDesign, designId, content);
          useSelectedDesignStore.getState().setDesignId(clonedDesignId);
          toast.success('Design cloned successfully! Now editing the cloned design.', { duration: 5000 });
        } else {
          toast.success('Failed to clone design! Could not save changes.', { duration: 5000 });
        }
      } else {
        await saveCurrentDesign(currentDesign, designId, content);
      }
    } else {
      await saveCurrentDesign(currentDesign, designId, content);
    }
    saveSettings = getDefaultSaveSettings();
  }
  else {
    if (!settings?.isUndo && isMeaningful) pushDesignToStack(currentDesign, saveSettings);
    autosaveTimeout = setTimeout(async () => {
      if (editingForeignDesign) {
        var saveAsNewForAdmin = mustClone ? true : restoreSaveAsNewForAdmin();
        if (saveAsNewForAdmin === null) {
          saveAsNewForAdmin = await showSaveAlertDialog();
          storeSaveAsNewForAdmin(saveAsNewForAdmin);
          isShowingSaveDialog = false;
        }
        if (saveAsNewForAdmin) {
          const clonedDesignId = await cloneDesignByDesignId(api, designId);
          if (clonedDesignId) {
            designId = clonedDesignId;
            saveCurrentDesign(currentDesign, designId, content).then(() => {
              saveSettings = getDefaultSaveSettings();
              useSelectedDesignStore.getState().setDesignId(clonedDesignId);
              toast.success('Design cloned successfully. Now editing the cloned design.', { duration: 5000 });
            });
          } else {
            toast.success('Failed to clone design! Could not save changes.', { duration: 5000 });
          }
        } else {
          saveCurrentDesign(currentDesign, designId, content).then(() => {
            saveSettings = getDefaultSaveSettings();
          });
        }
      } else {
        saveCurrentDesign(currentDesign, designId, content).then(() => {
          saveSettings = getDefaultSaveSettings();
        });
      }
    }, 5000);
  }
};

export const saveCurrentDesign = async (
  currentDesign: InsoleDesign,
  designId: number,
  content: string
) => {
  try {
    await saveDesign(api, currentDesign, designId, saveSettings);
    useViewerStore.setState({ newDesign: false });
    console.log(`Design: ${designId} saved successfully`);
  } catch (error) {
    console.error('Error saving design:', error);
    toast.error('Failed to save changes.', { duration: 10000 });
  }
};

export const applyTemplate = async (
  templateDesign: InsoleDesign,
  previousDesign: InsoleDesign,
  actionType: ActionType = ActionType.others,
  save: boolean = true) => {
  {/*Overwrite settings*/ }
  let overwriteSettings = {
    newAddition: false,
    updateAddition: false,
    removeAddition: false,
    updateInsoleMesh: true,
    updateDesignInfo: true,
    updateAutoProperties: true,
    replaceAdditons: true,
  };
  // const copySaveSettings: SaveSettings = { ...saveSettings };

  {/*Copy template design to previous design*/ }
  if (!templateDesign || !previousDesign) return;
  const left = templateDesign.insoles.find(ins => ins.side === 'left');
  const right = templateDesign.insoles.find(ins => ins.side === 'right');
  if (!left || !right) return;

  const rightCloned = Insole.cloneFrom(right);
  const leftCloned = Insole.cloneFrom(left);

  previousDesign.insoles = [rightCloned, leftCloned];
  previousDesign.category = templateDesign.category;
  previousDesign.isSandal = templateDesign.category === DesignCategory.Sandal;
  previousDesign.insoles[0].additions = [];
  previousDesign.insoles[1].additions = [];

  for (const addition of right.additions) {
    const cloned = Addition.cloneFrom(addition);
    previousDesign.insoles[0].additions.push(cloned);
  }
  for (const addition of left.additions) {
    const cloned = Addition.cloneFrom(addition);
    previousDesign.insoles[1].additions.push(cloned);
  }
  if (templateDesign.autoProperties) previousDesign.autoProperties = AutoProperties.cloneFrom(templateDesign.autoProperties);

  {/*Load previous design To Viewer*/ }
  useDesignStore.getState().setDesign(previousDesign);
  useDesignStore.setState({ designLoaded: true });
  const previousEngraving = useDesignStore.getState().previousEngraving;
  if (previousEngraving) useDesignStore.getState().setEngraving(previousEngraving);
  const previousDesignId = useSelectedDesignStore.getState().previousDesignId;

  {/*Reset Undo Stack*/ }
  const temporaryDesign = useDesignStore.getState().temporaryDesign;
  if (temporaryDesign) {
    temporaryDesign.designType = DesignType.General;
    resetStackWithDesign(temporaryDesign);

    if (save) {
      Object.assign(saveSettings, overwriteSettings);
      saveSettings.now = true;
      saveCurrentDesign(previousDesign, previousDesignId, 'Template replaced');
      if (actionType !== ActionType.others) pushDesignToStack(previousDesign, saveSettings);
      saveSettings = getDefaultSaveSettings();
    }
  }

  {/*Update Database and states*/ }
  ApplyGcodeSettingFromTemplate(api, useSelectedDesignStore.getState().designId, previousDesignId);
  useSelectedDesignStore.setState({ templateReplaced: true });
  useSelectedDesignStore.getState().setDesignId(previousDesignId);
  // Object.assign(saveSettings, copySaveSettings);
  // saveCurrentDesign(previousDesign, previousDesignId, 'Template replaced');
  useSelectedDesignStore.setState({ previousDesignId: -1 });
  toast.success('Template applied. Continue with your updated design.', { duration: 5000 });
}

export const discardTemplate = async (previousDesign: InsoleDesign) => {
  useDesignStore.getState().setDesign(previousDesign);
  useDesignStore.setState({ designLoaded: true });
  const previousDesignId = useSelectedDesignStore.getState().previousDesignId;
  useSelectedDesignStore.setState({ templateReplaced: true });
  useSelectedDesignStore.getState().setDesignId(previousDesignId);
  useSelectedDesignStore.setState({ previousDesignId: -1 });
  resetStackWithDesign(previousDesign);
  useDesignStore.setState({ temporaryDesign: null });
  toast.error('No changes made. Current design is preserved.', { duration: 10000 });
}

const showSaveAlertDialog = async (): Promise<boolean> => {
  isShowingSaveDialog = true;
  return showAlertDialog({
    title: 'Unsaved Changes',
    description: 'Do you want to save your changes as a new design?',
    agreeText: 'Yes, Clone Current Design',
    disagreeText: 'No, Save Without Cloning',
  });
}

const storeSaveAsNewForAdmin = (saveAsNewForAdmin: boolean | null) => {
  if (saveAsNewForAdmin === null) {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.SAVE_AS_NEW_FOR_ADMIN);
    return;
  }
  localStorage.setItem(LOCAL_STORAGE_KEYS.SAVE_AS_NEW_FOR_ADMIN, saveAsNewForAdmin.toString());
}

const restoreSaveAsNewForAdmin = (): boolean | null => {
  const saveAsNewForAdmin = localStorage.getItem(LOCAL_STORAGE_KEYS.SAVE_AS_NEW_FOR_ADMIN);
  if (saveAsNewForAdmin === null || saveAsNewForAdmin === '') return null;
  return saveAsNewForAdmin === 'true';
}
